import type { Coordinate, Inventory, WeaponType } from "./models"
import type { ItemTypes } from "./item/item_type"

/** Data only: campaigns may register profiles; scripts refer to their ids. Distances are pixels. */
export interface ChargedWeaponDefinition {
    id: string
    weaponType: WeaponType
    chargeMs: number
    cooldownMs: number
    movementMultiplier: number
    maxRange: number
    projectileSpeed: number
    fuseMs: number
    blast: {
        radius: number
        centerDamage: number
        edgeDamage: number
        targets: "hostile" | "non-friendly" | "all"
        selfDamageMultiplier: number
        blockedByWalls: boolean
    }
    /** Lobbed shots cross blocking terrain; straight shots retain collision tracing. */
    trajectory?: "straight" | "lob"
    appearance?: "energy" | "bomb"
    /** Optional damage to authored structure types, resolved once per parent cell. */
    structureDamage?: { types: string[]; centerDamage: number; edgeDamage: number }
    ammunition: { items: ItemTypes[]; quantity: number }
}

export interface ChargedWeaponAction {
    chargeAction: "begin" | "release" | "cancel"
    chargeId: string
    target?: Coordinate
}

export interface WeaponChargeView {
    id: string
    durationMs: number
    remainingMs: number
    movementMultiplier: number
}

export interface NpcAreaAttackDefinition {
    projectile: ChargedWeaponDefinition
    throwMs: number
    recoveryMs: number
    preferredTileTypes: string[]
    /** Zero-based LPC frame, reached over raiseMs and held until authoritative release. */
    windupHoldFrame?: number
    windupRaiseMs?: number
}

/** General authoritative body pose and locked target warning, independent of campaigns. */
export interface AttackWindupView {
    id: string
    phase: "windup" | "throw" | "recovery"
    angle: number
    target: Coordinate
    radius: number
    durationMs: number
    remainingMs: number
}

export interface AreaProjectileView {
    appearance?: "energy" | "bomb"
    /** Normalized authoritative flight progress, used to lift the visual above its ground point. */
    flightProgress?: number
    destination?: Coordinate
    phase: "flight" | "fuse" | "blast"
    radius: number
    durationMs: number
    remainingMs: number
}

export const EMBER_CORE: ChargedWeaponDefinition = {
    id: "ember-core", weaponType: "staff",
    chargeMs: 800, cooldownMs: 2000, movementMultiplier: 0.5,
    maxRange: 192, projectileSpeed: 320, fuseMs: 350,
    blast: { radius: 64, centerDamage: 12, edgeDamage: 4,
        targets: "non-friendly", selfDamageMultiplier: 0, blockedByWalls: true },
    ammunition: { items: ["ember-charge"], quantity: 1 },
}

/** Copy and validate at registration. No live profile mutation can change an in-flight shot. */
export const copyChargedWeaponDefinition = (definition: ChargedWeaponDefinition): ChargedWeaponDefinition => {
    if (!definition || typeof definition.id !== "string" || !definition.id.length || definition.id.length > 80 ||
        typeof definition.weaponType !== "string") {
        throw new Error("Invalid charged weapon identity")
    }
    const bounded = (value: number, min: number, max: number) => Number.isFinite(value) && value >= min && value <= max
    if (!bounded(definition.chargeMs, 1, 30000) || !bounded(definition.cooldownMs, 0, 60000) ||
        !bounded(definition.movementMultiplier, 0, 1) || !bounded(definition.maxRange, 1, 1024) ||
        !bounded(definition.projectileSpeed, 1, 2048) || !bounded(definition.fuseMs, 0, 30000) ||
        !bounded(definition.blast?.radius, 1, 512) || !bounded(definition.blast.centerDamage, 0, 10000) ||
        !bounded(definition.blast.edgeDamage, 0, definition.blast.centerDamage) ||
        !bounded(definition.blast.selfDamageMultiplier, 0, 1) ||
        !["hostile", "non-friendly", "all"].includes(definition.blast.targets) ||
        typeof definition.blast.blockedByWalls !== "boolean" ||
        !Number.isInteger(definition.ammunition?.quantity) || !bounded(definition.ammunition.quantity, 1, 1000) ||
        !Array.isArray(definition.ammunition.items) || !definition.ammunition.items.length ||
        definition.ammunition.items.some(item => typeof item !== "string" || !item.length)) {
        throw new Error("Invalid charged weapon parameters")
    }
    if (definition.trajectory !== undefined && !["straight", "lob"].includes(definition.trajectory) ||
        definition.appearance !== undefined && !["energy", "bomb"].includes(definition.appearance) ||
        definition.structureDamage && (!Array.isArray(definition.structureDamage.types) ||
            !definition.structureDamage.types.length || definition.structureDamage.types.some(t => typeof t !== "string" || !t.length) ||
            !bounded(definition.structureDamage.centerDamage, 0, 10000) ||
            !bounded(definition.structureDamage.edgeDamage, 0, definition.structureDamage.centerDamage))) {
        throw new Error("Invalid area projectile parameters")
    }
    return { ...definition, structureDamage: definition.structureDamage && {
        ...definition.structureDamage, types: [...new Set(definition.structureDamage.types)] }, blast: { ...definition.blast },
        ammunition: { ...definition.ammunition, items: [...new Set(definition.ammunition.items)] } }
}

/** Plans all stack deductions before applying any, so insufficient ammo never partially spends. */
export const spendChargedWeaponAmmo = (inventory: Inventory, cost: ChargedWeaponDefinition["ammunition"], spend = true): boolean => {
    let remaining = cost.quantity
    const deductions: Array<{ item: Inventory["items"][number]; amount: number }> = []
    for (const item of inventory?.items || []) {
        if (!cost.items.includes(item.itemTypeMeta?.type as ItemTypes)) continue
        const amount = Math.min(Math.max(0, item.quantity || 0), remaining)
        if (amount > 0) deductions.push({ item, amount })
        remaining -= amount
        if (!remaining) break
    }
    if (remaining > 0) return false
    if (spend) deductions.forEach(({ item, amount }) => item.quantity -= amount)
    return true
}

/** Trace in <= 4px steps, stopping BEFORE blocking terrain, for both flight and blast visibility. */
export const traceAreaProjectile = (from: Coordinate, to: Coordinate, blocked: (point: Coordinate) => boolean): Coordinate => {
    const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / 4))
    let last = { ...from }
    for (let i = 1; i <= steps; i++) {
        const point = { x: from.x + (to.x - from.x) * i / steps, y: from.y + (to.y - from.y) * i / steps }
        if (blocked(point)) break
        last = point
    }
    return last
}
